import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Share2, X, Copy, Check, ChevronDown } from 'lucide-react';
import SmartCalculatorsSidebar from '../../components/common/SmartCalculatorsSidebar';
import Navbar from '../../components/common/Navbar';

const HRACalculator = () => {
  const [basicStr, setBasicStr]   = useState('600000');
  const [daStr, setDaStr]         = useState('0');
  const [hraStr, setHraStr]       = useState('240000');
  const [rentStr, setRentStr]     = useState('180000');
  const [isMetro, setIsMetro]     = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied]       = useState(false);
  const [openFaq, setOpenFaq]     = useState(null);

  const basic = parseInt(basicStr) || 0;
  const da    = parseInt(daStr)    || 0;
  const hra   = parseInt(hraStr)   || 0;
  const rent  = parseInt(rentStr)  || 0;

  const { actualHRA, salaryPercent, excessRent, exemptedHRA, taxableHRA } = useMemo(() => {
    const salaryBase = basic + da;
    const pct        = isMetro ? 0.5 : 0.4;
    const a1 = hra;
    const a2 = Math.round(salaryBase * pct);
    const a3 = Math.max(rent - Math.round(salaryBase * 0.1), 0);
    const exempted = Math.max(Math.min(a1, a2, a3), 0);
    return {
      actualHRA:     a1,
      salaryPercent: a2,
      excessRent:    a3,
      exemptedHRA:   exempted,
      taxableHRA:    Math.max(hra - exempted, 0),
    };
  }, [basic, da, hra, rent, isMetro]);

  const fmt = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

  const fmtC = (n) => {
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
    if (n >= 100000)   return `₹${(n / 100000).toFixed(1)}L`;
    return `₹${(n / 1000).toFixed(0)}K`;
  };

  const sliderBg = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return `linear-gradient(to right, #115e59 0%, #115e59 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`;
  };

  const handleShare = (platform) => {
    const text = encodeURIComponent('Calculate your HRA exemption with Kapitalised: kapitalised.com/calculators/hra');
    if (platform === 'copy') {
      navigator.clipboard.writeText('https://kapitalised.com/calculators/hra');
      setCopied(true); setTimeout(() => setCopied(false), 2000); return;
    }
    const maps = { whatsapp: `https://wa.me/?text=${text}`, twitter: `https://twitter.com/intent/tweet?text=${text}` };
    window.open(maps[platform], '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  const labelSt    = { fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 500, color: '#374151' };
  const valueSt    = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 600, color: '#115e59', border: 'none', outline: 'none', background: 'transparent', textAlign: 'right' };
  const suffixSt   = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 500, color: '#9ca3af' };
  const minMaxSt   = { fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af' };
  const inputBoxSt = { display: 'flex', alignItems: 'center', gap: '3px', backgroundColor: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '6px 12px' };

  const css = `
    .hra-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 5px; border-radius: 3px; outline: none; cursor: pointer; }
    .hra-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,0.15); }
    .hra-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; }
    .no-spin::-webkit-outer-spin-button, .no-spin::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    .no-spin { -moz-appearance: textfield; }
    .hra-card { padding: 36px; }
    .hra-seo  { background-color: #ffffff; padding: 48px 450px 64px 100px; }
    .hra-two-col { display: flex; gap: 24px; align-items: flex-start; }
    .hra-result-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 14px; }
    @media (min-width: 640px) and (max-width: 1023px) {
      .hero-section { padding: 32px 40px 80px 40px !important; }
      .hra-seo { padding: 48px 40px 64px 40px !important; }
      .hra-card { padding: 28px !important; }
      .hra-result-grid { grid-template-columns: 1fr 1fr !important; }
    }
    @media (max-width: 639px) {
      .hra-two-col { flex-direction: column !important; }
      .hra-card { padding: 20px !important; }
      .hero-section { padding: 32px 20px 60px 20px !important; }
      .hra-seo { padding: 32px 20px 48px 20px !important; }
      .hra-result-grid { grid-template-columns: 1fr 1fr !important; }
    }
  `;

  const faqs = [
    { q: 'Is HRA exemption available under the new tax regime?', a: 'No. HRA exemption under Section 10(13A) is only available if you opt for the old tax regime. Under the new tax regime (default from FY 2023-24), HRA is fully taxable as part of your salary.' },
    { q: 'Can I claim HRA if I pay rent to my parents?', a: 'Yes, you can pay rent to your parents and claim HRA exemption. Your parents must declare this as rental income in their tax returns. A rent agreement and receipts should be maintained. This is a legitimate and commonly used tax planning strategy.' },
    { q: 'Do I need rent receipts to claim HRA?', a: "Rent receipts are not required to be submitted to your employer unless your annual HRA claim exceeds ₹1 lakh. However, if the rent paid exceeds ₹1 lakh per year, you must furnish the landlord's PAN to your employer." },
    { q: 'What are the metro cities for HRA calculation?', a: 'For HRA purposes, metro cities are Delhi, Mumbai, Kolkata, and Chennai. If you reside in any of these cities, 50% of basic salary is used in the HRA exemption calculation. For all other cities, 40% is used.' },
    { q: 'Can I claim both HRA and home loan deduction?', a: 'Yes. If you own a house but live in a rented house (e.g., your house is in a different city), you can claim both HRA exemption on your rent and home loan interest deduction under Section 24(b) simultaneously.' },
  ];

  // Donut chart for exempted vs taxable
  const R = 70, CX = 100, CY = 100;
  const circ     = 2 * Math.PI * R;
  const gap      = 5;
  const total    = exemptedHRA + taxableHRA || 1;
  const exPct    = exemptedHRA / total;
  const txPct    = taxableHRA  / total;
  const exDash   = Math.max(exPct * circ - gap, 0);
  const txDash   = Math.max(txPct * circ - gap, 0);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Onest:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>{css}</style>
      <Navbar background="#1a4d3e" />

      {/* Hero */}
      <div className="hero-section" style={{ background: 'linear-gradient(180deg, #1a4d3e 0%, #2d6b5a 30%, #4a9d7f 60%, #7ec9a8 85%, #ffffff 100%)', padding: '32px 100px 80px 100px' }}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: '18px' }}>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontWeight: 400 }}>Home</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontWeight: 400 }}>Calculators</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>HRA Calculator</span>
        </div>

        {/* Title + Share */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <h1 style={{ fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: '62px', color: '#ffffff', margin: 0, lineHeight: 1 }}>HRA Calculator</h1>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              style={{ fontFamily: 'Onest, sans-serif', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#ffffff', fontWeight: 600, fontSize: '14px', paddingBottom: '8px' }}
            >
              <Share2 size={16} /> SHARE
            </button>
            {showShareMenu && (
              <div style={{ position: 'absolute', right: 0, top: '36px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', padding: '12px', zIndex: 50, minWidth: '220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontFamily: 'Onest', fontWeight: 600, fontSize: '13px', color: '#111827' }}>Share via</span>
                  <button onClick={() => setShowShareMenu(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={16} /></button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  {[
                    { id: 'whatsapp', label: 'WhatsApp', bg: '#25D366', icon: <svg width="20" height="20" fill="white" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
                    { id: 'twitter',  label: 'X',        bg: '#000',     icon: <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                    { id: 'copy',     label: copied ? 'Copied!' : 'Copy', bg: '#e5e7eb', icon: copied ? <Check size={18} color="#16a34a" /> : <Copy size={18} color="#374151" /> },
                  ].map(({ id, label, bg, icon }) => (
                    <button key={id} onClick={() => handleShare(id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '8px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
                      <span style={{ fontFamily: 'Onest', fontSize: '11px', color: '#6b7280' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Two-col layout */}
        <div className="hra-two-col">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="hra-card" style={{ backgroundColor: '#ffffff', borderRadius: '24px', boxShadow: '0 8px 48px rgba(0,0,0,0.18)' }}>

              {/* Old regime note */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fffbeb', border: '1.5px solid #fde68a', borderRadius: '50px', padding: '5px 14px', marginBottom: '28px' }}>
                <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', fontWeight: 600, color: '#92400e' }}>Old Tax Regime only — not applicable under new regime</span>
              </div>

              {/* Basic Salary */}
              <HraSliderRow
                label="Basic Salary (per year)"
                valueStr={basicStr}
                onInput={setBasicStr}
                min={60000} max={3000000} step={12000}
                minLabel="₹60K" maxLabel="₹30L"
                prefix="₹"
                labelSt={labelSt} valueSt={valueSt} suffixSt={suffixSt} minMaxSt={minMaxSt} inputBoxSt={inputBoxSt}
                sliderBg={sliderBg}
              />

              {/* DA */}
              <HraSliderRow
                label="Dearness Allowance (per year)"
                valueStr={daStr}
                onInput={setDaStr}
                min={0} max={1500000} step={12000}
                minLabel="₹0" maxLabel="₹15L"
                prefix="₹"
                hint="Mainly for govt/PSU employees. Private sector: enter 0."
                labelSt={labelSt} valueSt={valueSt} suffixSt={suffixSt} minMaxSt={minMaxSt} inputBoxSt={inputBoxSt}
                sliderBg={sliderBg}
              />

              {/* HRA Received */}
              <HraSliderRow
                label="HRA Received (per year)"
                valueStr={hraStr}
                onInput={setHraStr}
                min={12000} max={1500000} step={12000}
                minLabel="₹12K" maxLabel="₹15L"
                prefix="₹"
                labelSt={labelSt} valueSt={valueSt} suffixSt={suffixSt} minMaxSt={minMaxSt} inputBoxSt={inputBoxSt}
                sliderBg={sliderBg}
              />

              {/* Rent Paid */}
              <HraSliderRow
                label="Total Rent Paid (per year)"
                valueStr={rentStr}
                onInput={setRentStr}
                min={0} max={1500000} step={12000}
                minLabel="₹0" maxLabel="₹15L"
                prefix="₹"
                isLast
                labelSt={labelSt} valueSt={valueSt} suffixSt={suffixSt} minMaxSt={minMaxSt} inputBoxSt={inputBoxSt}
                sliderBg={sliderBg}
              />

              {/* Metro toggle */}
              <div style={{ marginTop: '28px', marginBottom: '10px' }}>
                <label style={{ ...labelSt, display: 'block', marginBottom: '10px' }}>City Type</label>
                <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '10px', padding: '3px', width: 'fit-content' }}>
                  {[
                    { label: 'Metro (50%)', value: true },
                    { label: 'Non-Metro (40%)', value: false },
                  ].map(({ label, value }) => (
                    <button key={String(value)} onClick={() => setIsMetro(value)}
                      style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', fontWeight: 500, padding: '8px 18px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: isMetro === value ? '#115e59' : 'transparent', color: isMetro === value ? '#fff' : '#6b7280', transition: 'all 0.15s', whiteSpace: 'nowrap' }}>
                      {label}
                    </button>
                  ))}
                </div>
                <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                  Metro: Delhi, Mumbai, Kolkata, Chennai
                </p>
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1.5px solid #f3f4f6', margin: '32px 0' }} />

              {/* Chart + Results */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '36px' }}>

                {/* Donut */}
                <div style={{ flexShrink: 0 }}>
                  <svg viewBox="0 0 200 200" width="180" height="180">
                    <g transform={`rotate(-90, ${CX}, ${CY})`}>
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#f3f4f6" strokeWidth="28" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#115e59" strokeWidth="28"
                        strokeDasharray={`${exDash} ${circ}`} strokeLinecap="butt" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#4ade80" strokeWidth="28"
                        strokeDasharray={`${txDash} ${circ}`} strokeDashoffset={-(exDash + gap)} strokeLinecap="butt" />
                    </g>
                    <text x={CX} y={CY - 6} textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', fill: '#9ca3af', fontWeight: 500 }}>Tax-Free</text>
                    <text x={CX} y={CY + 14} textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', fill: '#111827', fontWeight: 700 }}>{fmtC(exemptedHRA)}</text>
                  </svg>
                </div>

                {/* Result rows */}
                <div style={{ flex: 1 }}>
                  {[
                    { label: 'Actual HRA Received',           value: actualHRA,     dot: '#e5e7eb', sub: null },
                    { label: `${isMetro ? '50%' : '40%'} of Basic + DA`, value: salaryPercent, dot: '#e5e7eb', sub: null },
                    { label: 'Rent − 10% of Salary',          value: excessRent,    dot: '#e5e7eb', sub: null },
                  ].map(({ label, value, dot }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: dot, border: '1.5px solid #d1d5db', flexShrink: 0 }} />
                        <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', color: '#6b7280', fontWeight: 500 }}>{label}</span>
                      </div>
                      <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', fontWeight: 700, color: '#374151' }}>{fmt(value)}</span>
                    </div>
                  ))}

                  <div style={{ borderTop: '1px solid #f3f4f6', margin: '14px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#115e59', flexShrink: 0 }} />
                      <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>HRA Exempted</span>
                    </div>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '22px', fontWeight: 800, color: '#115e59', letterSpacing: '-0.02em' }}>{fmt(exemptedHRA)}</span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#4ade80', flexShrink: 0 }} />
                      <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Taxable HRA</span>
                    </div>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 700, color: taxableHRA > 0 ? '#dc2626' : '#9ca3af' }}>{fmt(taxableHRA)}</span>
                  </div>

                  <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', marginTop: '10px', lineHeight: 1.5 }}>
                    Exemption = least of the 3 values above
                  </p>
                </div>
              </div>

              {taxableHRA > 0 && (
                <div style={{ marginTop: '20px', padding: '12px 16px', background: '#fffbeb', borderRadius: '10px', border: '1px solid #fde68a' }}>
                  <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#92400e', margin: 0, lineHeight: 1.5 }}>
                    <strong>Tip:</strong> Increasing your rent or moving to a metro city can reduce your taxable HRA. If annual rent exceeds ₹1 lakh, collect your landlord's PAN.
                  </p>
                </div>
              )}

            </div>
          </div>

          <SmartCalculatorsSidebar currentSlug="hra" rightOffset="100px" />
        </div>
      </div>

      {/* SEO / FAQ */}
      <div className="hra-seo">
        <div style={{ marginBottom: '48px' }}>
          <h2 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '36px', color: '#1f2937', marginBottom: '16px' }}>
            HRA Calculator – Calculate House Rent Allowance Exemption
          </h2>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            The Kapitalised HRA Calculator helps salaried individuals in India calculate their HRA exemption under Section 10(13A) of the Income Tax Act. Enter your basic salary, dearness allowance, HRA received, and annual rent paid to find out exactly how much HRA is tax-free and how much is taxable.
          </p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59', color: '#ffffff', padding: '14px 32px', borderRadius: '8px', fontWeight: 600, fontSize: '15px', border: 'none', cursor: 'pointer' }}>
            Calculate Your HRA
          </button>
        </div>

        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>HRA Exemption Formula</h3>
          <div style={{ backgroundColor: '#115e59', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
            <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', fontWeight: 700, color: '#ffffff', marginBottom: '16px' }}>
              Exempted HRA = Minimum of:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                ['1.', 'Actual HRA received from employer'],
                ['2.', 'Rent paid − 10% of (Basic + DA)'],
                ['3.', '50% of (Basic + DA) for metro cities, 40% for non-metro'],
              ].map(([num, desc]) => (
                <div key={num} style={{ backgroundColor: 'rgba(255,255,255,0.12)', padding: '10px 14px', borderRadius: '8px', display: 'flex', gap: '10px' }}>
                  <span style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, color: '#4ade80', fontSize: '16px', flexShrink: 0 }}>{num}</span>
                  <span style={{ fontFamily: 'Onest, sans-serif', color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>{desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <h2 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '24px', color: '#1f2937', marginBottom: '20px' }}>Frequently Asked Questions</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {faqs.map(({ q, a }, i) => (
            <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: openFaq === i ? '#f9fafb' : '#fff', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>{q}</span>
                <ChevronDown size={16} color="#6b7280" style={{ transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0, marginLeft: '12px' }} />
              </button>
              {openFaq === i && (
                <div style={{ padding: '0 20px 16px 20px', background: '#f9fafb' }}>
                  <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>{a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Slider row for HRA inputs
const HraSliderRow = ({ label, valueStr, onInput, min, max, step, minLabel, maxLabel, prefix, isLast, hint, labelSt, valueSt, suffixSt, minMaxSt, inputBoxSt, sliderBg }) => {
  const numVal = parseInt(valueStr) || 0;
  const clampedVal = Math.min(Math.max(numVal, min), max);
  return (
    <div style={{ marginBottom: isLast ? 0 : '30px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div>
          <label style={labelSt}>{label}</label>
          {hint && <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', margin: '2px 0 0 0' }}>{hint}</p>}
        </div>
        <div style={inputBoxSt}>
          {prefix && <span style={suffixSt}>{prefix}</span>}
          <input
            type="text"
            inputMode="numeric"
            className="no-spin"
            value={valueStr}
            onChange={(e) => onInput(e.target.value.replace(/[^0-9]/g, ''))}
            style={{ ...valueSt, width: '90px' }}
          />
        </div>
      </div>
      <input
        type="range"
        className="hra-slider"
        min={min} max={max} step={step}
        value={clampedVal}
        onChange={(e) => onInput(e.target.value)}
        style={{ background: sliderBg(clampedVal, min, max) }}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
        <span style={minMaxSt}>{minLabel}</span>
        <span style={minMaxSt}>{maxLabel}</span>
      </div>
    </div>
  );
};

export default HRACalculator;
